/*
 * Zoek-dropdown (mega menu-stijl). Hoort bij snippets/header-search.liquid,
 * sections/mega-search-results.liquid en assets/header-search.css.
 *
 * - Neemt de zoek-knoppen van het thema over ([aria-controls="search"]), zodat de
 *   standaard zoekbalk en de app niet meer openen.
 * - Opent direct onder de header: fade + 20px van boven, zelfde timing als de menu's.
 * - Zonder zoekterm: populaire zoektermen/categorieën + populaire producten.
 * - Rustige overgangen: hoogte animeert, nieuwe inhoud fade in, afbeeldingen faden in
 *   zodra ze geladen zijn en de laad-status verschijnt pas bij een trage respons.
 * - Typen: Shopify Predictive Search (section_id=mega-search-results), met debounce,
 *   cache per zoekterm en afbreken van verouderde verzoeken.
 */
(function () {
  'use strict';

  var drawer = document.getElementById('ms-search');
  if (!drawer) return;

  var html = document.documentElement;
  var input = drawer.querySelector('.ms-search-input');
  var form = drawer.querySelector('.ms-search-form');
  var body = drawer.querySelector('.ms-search-body');
  var clearBtn = drawer.querySelector('[data-ms-search-clear]');
  var defaultView = drawer.querySelector('[data-ms-search-default]');
  var resultsView = drawer.querySelector('[data-ms-search-results]');
  var suggestUrl = drawer.dataset.suggestUrl || '/search/suggest';
  var searchUrl = (form && form.getAttribute('action')) || '/search';

  var HEIGHT_MS = 300;
  var LOADING_DELAY = 250;
  var DEBOUNCE = 200;
  var TRIGGER = '[aria-controls="search"], [data-ms-search-open]';
  var lastTrigger = null;
  var debounceTimer = null;
  var controller = null;
  var cache = {};
  var currentTerm = '';
  var heightTimer = null;
  var loadingTimer = null;
  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  var RECENT_KEY = 'ms-search-recent';
  var RECENT_MAX = 5;
  var recentBox = drawer.querySelector('[data-ms-search-recent]');

  drawer.classList.add('is-ready');

  // Onderrand van de header = bovenkant van de dropdown
  function headerEl() {
    return document.getElementById('header') || document.querySelector('.shopify-section-header');
  }

  function setTop() {
    var header = headerEl();
    if (!header) return;
    var headerRect = header.getBoundingClientRect();
    drawer.style.setProperty('--ms-search-top', Math.max(0, headerRect.bottom) + 'px');
    // Zoekbalk even hoog als de header
    drawer.style.setProperty('--ms-header-h', headerRect.height + 'px');
    // Oranje balk onder het zoekicoon (zoals bij een geopend menu-item)
    var icon = document.querySelector('#nav-user li.search > a > i') || document.querySelector('#nav-user li.search > a');
    if (icon) {
      var rect = icon.getBoundingClientRect();
      drawer.style.setProperty('--ms-search-icon-left', rect.left + 'px');
      drawer.style.setProperty('--ms-search-icon-width', rect.width + 'px');
    }
  }

  function isOpen() {
    return html.classList.contains('ms-search-open');
  }

  // Header kan na het openen nog van hoogte veranderen; bovenkant blijven bijhouden
  (function watchHeader() {
    var header = headerEl();
    if (!header || !window.ResizeObserver) return;
    new ResizeObserver(function () {
      if (isOpen()) setTop();
    }).observe(header);
  })();
  window.addEventListener('load', function () {
    if (isOpen()) setTop();
  });

  function open(trigger) {
    lastTrigger = trigger || null;
    setTop();
    requestAnimationFrame(setTop);
    setTimeout(setTop, 300);
    drawer.removeAttribute('inert');
    drawer.setAttribute('aria-hidden', 'false');
    html.classList.add('ms-search-open');
    document.dispatchEvent(new CustomEvent('ms:search-open'));
    setExpanded(true);
    // Direct focussen (binnen de klik) zodat het toetsenbord op mobiel ook opent. De CSS maakt
    // de dropdown bij openen meteen visible (geen visibility-transitie), anders faalt focus().
    input.focus({ preventScroll: true });
    markLoadedImages(drawer);
    updateAllArrows();
  }

  function close(returnFocus) {
    if (!isOpen()) return;
    html.classList.remove('ms-search-open');
    drawer.setAttribute('aria-hidden', 'true');
    drawer.setAttribute('inert', '');
    setExpanded(false);
    if (document.activeElement === input) input.blur();
    if (returnFocus !== false && lastTrigger && lastTrigger.focus) lastTrigger.focus({ preventScroll: true });
  }

  function setExpanded(state) {
    Array.prototype.forEach.call(document.querySelectorAll(TRIGGER), function (el) {
      if (drawer.contains(el)) return;
      el.setAttribute('aria-expanded', state ? 'true' : 'false');
    });
  }

  // ---- Recent gezocht (blijft in de browser van de bezoeker) ---------------------
  function readRecent() {
    try {
      var stored = JSON.parse(window.localStorage.getItem(RECENT_KEY) || '[]');
      return Array.isArray(stored) ? stored.filter(function (t) { return typeof t === 'string'; }) : [];
    } catch (e) {
      return [];
    }
  }

  function saveRecent(term) {
    term = (term || '').trim();
    if (!term || !recentBox) return;
    var list = readRecent().filter(function (t) { return t.toLowerCase() !== term.toLowerCase(); });
    list.unshift(term);
    list = list.slice(0, RECENT_MAX);
    try { window.localStorage.setItem(RECENT_KEY, JSON.stringify(list)); } catch (e) {}
    renderRecent();
  }

  function clearRecent() {
    try { window.localStorage.removeItem(RECENT_KEY); } catch (e) {}
    renderRecent();
  }

  function renderRecent() {
    if (!recentBox) return;
    var list = readRecent();
    if (!list.length) {
      recentBox.hidden = true;
      recentBox.innerHTML = '';
      return;
    }
    var items = list
      .map(function (term) {
        var safe = term.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
        return '<li><a href="' + searchUrl + '?q=' + encodeURIComponent(term) +
          '&options%5Bprefix%5D=last" data-ms-search-term="' + safe + '">' + safe + '</a></li>';
      })
      .join('');
    recentBox.innerHTML =
      '<p class="ms-search-title">' + recentBox.dataset.title +
      '<button type="button" class="ms-search-recent-clear" data-ms-search-recent-clear>' +
      recentBox.dataset.clear + '</button></p><ul class="ms-search-links">' + items + '</ul>';
    recentBox.hidden = false;
  }
  renderRecent();

  // ---- Afbeeldingen faden in zodra ze geladen zijn ------------------------------
  function markLoadedImages(scope) {
    Array.prototype.forEach.call(scope.querySelectorAll('.ms-card-img img:not(.is-loaded)'), function (img) {
      if (img.complete && img.naturalWidth) img.classList.add('is-loaded');
    });
  }
  drawer.addEventListener('load', function (event) {
    if (event.target.tagName === 'IMG') event.target.classList.add('is-loaded');
  }, true);
  drawer.addEventListener('error', function (event) {
    if (event.target.tagName === 'IMG') event.target.classList.add('is-loaded');
  }, true);

  // ---- Hoogte animeren bij het wisselen van inhoud (desktop; mobiel is het paneel vast) ----
  function swapContent(change) {
    var from = body.getBoundingClientRect().height;
    clearTimeout(heightTimer);
    body.style.height = '';
    change();
    var to = body.getBoundingClientRect().height;
    if (reduceMotion.matches || Math.abs(to - from) < 2) {
      body.classList.remove('is-resizing');
      return;
    }
    body.classList.add('is-resizing');
    body.style.height = from + 'px';
    void body.offsetHeight;
    body.style.height = to + 'px';
    heightTimer = setTimeout(function () {
      body.style.height = '';
      body.classList.remove('is-resizing');
    }, HEIGHT_MS);
  }

  function fadeIn(el) {
    if (!el || reduceMotion.matches) return;
    el.classList.remove('is-entering');
    void el.offsetWidth;
    el.classList.add('is-entering');
  }

  function setLoading(state) {
    clearTimeout(loadingTimer);
    if (state) {
      // Alleen dimmen als de respons echt even duurt, anders knippert het bij elke letter
      loadingTimer = setTimeout(function () { drawer.classList.add('is-loading'); }, LOADING_DELAY);
    } else {
      drawer.classList.remove('is-loading');
    }
  }

  // ---- Zoeken -----------------------------------------------------------------
  function showDefault() {
    currentTerm = '';
    if (controller) controller.abort();
    setLoading(false);
    drawer.classList.remove('is-searching');
    clearBtn.hidden = true;
    if (!defaultView.hidden) return;
    swapContent(function () {
      resultsView.hidden = true;
      defaultView.hidden = false;
      updateAllArrows();
    });
    fadeIn(defaultView);
  }

  function render(markup) {
    setLoading(false);
    swapContent(function () {
      resultsView.innerHTML = markup;
      // Geen resultaten: populaire producten eronder, zodat het paneel gevuld blijft
      var empty = resultsView.querySelector('.ms-search-empty');
      var popular = defaultView.querySelector('[data-ms-search-popular]');
      if (empty && popular) empty.parentNode.appendChild(popular.cloneNode(true));
      defaultView.hidden = true;
      resultsView.hidden = false;
      markLoadedImages(resultsView);
      updateAllArrows();
    });
    fadeIn(resultsView.firstElementChild);
    body.scrollTop = 0;
  }

  function search(term) {
    currentTerm = term;
    clearBtn.hidden = false;
    drawer.classList.add('is-searching');
    if (cache[term] !== undefined) {
      render(cache[term]);
      return;
    }
    if (controller) controller.abort();
    controller = window.AbortController ? new AbortController() : null;
    setLoading(true);
    var params =
      'q=' + encodeURIComponent(term) +
      '&' + encodeURIComponent('resources[type]') + '=product,collection,query,page,article' +
      '&' + encodeURIComponent('resources[limit]') + '=10' +
      '&' + encodeURIComponent('resources[limit_scope]') + '=each' +
      '&' + encodeURIComponent('resources[options][prefix]') + '=last' +
      '&section_id=mega-search-results';
    fetch(suggestUrl + '?' + params, controller ? { signal: controller.signal } : {})
      .then(function (response) {
        if (!response.ok) throw new Error(response.status);
        return response.text();
      })
      .then(function (text) {
        var doc = new DOMParser().parseFromString(text, 'text/html');
        var section = doc.querySelector('.shopify-section');
        var markup = section ? section.innerHTML : '';
        cache[term] = markup;
        if (term === currentTerm) render(markup);
      })
      .catch(function (error) {
        if (error && error.name === 'AbortError') return;
        if (term === currentTerm) setLoading(false);
      });
  }

  input.addEventListener('input', function () {
    clearTimeout(debounceTimer);
    var term = input.value.trim();
    if (!term) {
      showDefault();
      return;
    }
    clearBtn.hidden = false;
    debounceTimer = setTimeout(function () { search(term); }, DEBOUNCE);
  });

  form.addEventListener('submit', function (event) {
    if (!input.value.trim()) {
      event.preventDefault();
      input.focus();
      return;
    }
    saveRecent(input.value);
  });

  // ---- Productpijltjes -----------------------------------------------------------
  function updateArrows(container) {
    var track = container && container.querySelector('.ms-cards');
    if (!track) return;
    container.classList.toggle('ms-no-scroll', track.scrollWidth <= track.clientWidth + 1);
  }
  function updateAllArrows() {
    Array.prototype.forEach.call(drawer.querySelectorAll('.ms-search-products'), updateArrows);
  }

  // ---- Openen / sluiten -----------------------------------------------------------
  // Capture: vóór de thema-listeners, zodat de standaard zoekbalk niet opent
  document.addEventListener('click', function (event) {
    var target = event.target;
    var trigger = target.closest && target.closest(TRIGGER);
    if (trigger && !drawer.contains(trigger)) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      if (isOpen()) close(false); else open(trigger);
      return;
    }
    // Ander element in de header terwijl de dropdown open is: sluiten, klik gaat door
    if (isOpen() && target.closest && target.closest('#header')) close(false);
  }, true);

  drawer.addEventListener('click', function (event) {
    var t = event.target;
    var el;
    if ((el = t.closest('[data-ms-search-close]'))) {
      event.preventDefault();
      close();
      return;
    }
    if ((el = t.closest('[data-ms-search-recent-clear]'))) {
      event.preventDefault();
      clearRecent();
      input.focus({ preventScroll: true });
      return;
    }
    if ((el = t.closest('[data-ms-search-clear]'))) {
      event.preventDefault();
      input.value = '';
      showDefault();
      input.focus();
      return;
    }
    if ((el = t.closest('[data-ms-search-term]'))) {
      // Zoekterm/suggestie: direct in de dropdown zoeken i.p.v. naar de zoekpagina
      event.preventDefault();
      input.value = el.dataset.msSearchTerm;
      saveRecent(input.value);
      clearTimeout(debounceTimer);
      search(input.value.trim());
      input.focus({ preventScroll: true });
      // Cursor achter de ingevulde term, zodat verder typen meteen kan
      var end = input.value.length;
      try { input.setSelectionRange(end, end); } catch (e) {}
      return;
    }
    if (t.closest('[data-ms-search-results] a[href]')) {
      // Doorgeklikt op een resultaat: de zoekterm onthouden
      saveRecent(input.value);
      return;
    }
    if ((el = t.closest('.ms-arrow'))) {
      event.preventDefault();
      var block = el.closest('.ms-search-products');
      var track = block && block.querySelector('.ms-cards');
      if (!track) return;
      var card = track.querySelector('.ms-card');
      var step = card ? card.getBoundingClientRect().width + 10 : track.clientWidth / 2;
      track.scrollBy({ left: el.classList.contains('ms-arrow-next') ? step : -step, behavior: 'smooth' });
    }
  });

  document.addEventListener('keydown', function (event) {
    if (event.key === 'Escape' && isOpen()) close();
  });
  // Mobiel menu opent: zoeken sluiten
  document.addEventListener('ms:menu-open', function () { close(false); });

  // Desktop: hover (of focus) op een menu-item met paneel sluit zoeken, het menu opent gewoon
  Array.prototype.forEach.call(document.querySelectorAll('#nav-bar > ul > li'), function (li) {
    if (!li.querySelector(':scope > [data-ms-panel], :scope > ul')) return;
    li.addEventListener('mouseenter', function () { if (isOpen()) close(false); });
    li.addEventListener('focusin', function () { if (isOpen()) close(false); });
  });

  window.addEventListener('resize', function () {
    if (!isOpen()) return;
    setTop();
    updateAllArrows();
  });
  window.addEventListener('scroll', function () {
    if (isOpen()) setTop();
  }, { passive: true });

  Array.prototype.forEach.call(document.querySelectorAll(TRIGGER), function (el) {
    if (drawer.contains(el)) return;
    el.setAttribute('aria-expanded', 'false');
  });
})();
